export type AmazonProfitInputs = {
  advertisingPercent: number;
  fulfillmentFee: number;
  landedCost: number;
  monthlyUnits: number;
  otherCost: number;
  referralFeePercent: number;
  returnsPercent: number;
  sellingPrice: number;
  storageFee: number;
};

export type AmazonProfitResult = {
  advertisingCost: number;
  amazonFees: number;
  breakEvenAcosPercent: number;
  monthlyProfit: number;
  monthlyRevenue: number;
  preAdvertisingProfit: number;
  profitMarginPercent: number;
  profitPerUnit: number;
  referralFee: number;
  returnsReserve: number;
  roiPercent: number | null;
  totalCostPerUnit: number;
};

const PERCENT_FIELDS = new Set<keyof AmazonProfitInputs>([
  'advertisingPercent',
  'referralFeePercent',
  'returnsPercent',
]);

export function normalizeAmazonProfitInput(
  key: keyof AmazonProfitInputs,
  value: string,
) {
  const numericValue = Number.parseFloat(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  if (numericValue < 0) {
    return '0';
  }

  if (PERCENT_FIELDS.has(key) && numericValue > 100) {
    return '100';
  }

  if (key === 'monthlyUnits') {
    return String(Math.floor(numericValue));
  }

  return value;
}

function asNonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function asPercent(value: number) {
  return Math.min(100, asNonNegative(value));
}

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function roundPercent(value: number) {
  return Math.round((value + Number.EPSILON) * 10) / 10;
}

export function calculateAmazonProfit(
  input: AmazonProfitInputs,
): AmazonProfitResult {
  const sellingPrice = asNonNegative(input.sellingPrice);
  const landedCost = asNonNegative(input.landedCost);
  const fulfillmentFee = asNonNegative(input.fulfillmentFee);
  const storageFee = asNonNegative(input.storageFee);
  const otherCost = asNonNegative(input.otherCost);
  const monthlyUnits = asNonNegative(input.monthlyUnits);
  const referralFeePercent = asPercent(input.referralFeePercent);
  const advertisingPercent = asPercent(input.advertisingPercent);
  const returnsPercent = asPercent(input.returnsPercent);

  const referralFee = roundCurrency(sellingPrice * (referralFeePercent / 100));
  const advertisingCost = roundCurrency(
    sellingPrice * (advertisingPercent / 100),
  );
  const returnsReserve = roundCurrency(sellingPrice * (returnsPercent / 100));
  const amazonFees = roundCurrency(referralFee + fulfillmentFee + storageFee);
  const preAdvertisingProfit = roundCurrency(
    sellingPrice - landedCost - amazonFees - returnsReserve - otherCost,
  );
  const profitPerUnit = roundCurrency(preAdvertisingProfit - advertisingCost);
  const totalCostPerUnit = roundCurrency(sellingPrice - profitPerUnit);
  const profitMarginPercent =
    sellingPrice > 0 ? roundPercent((profitPerUnit / sellingPrice) * 100) : 0;
  const roiPercent =
    landedCost > 0 ? roundPercent((profitPerUnit / landedCost) * 100) : null;
  const breakEvenAcosPercent =
    sellingPrice > 0
      ? roundPercent(Math.max(0, (preAdvertisingProfit / sellingPrice) * 100))
      : 0;

  return {
    advertisingCost,
    amazonFees,
    breakEvenAcosPercent,
    monthlyProfit: roundCurrency(profitPerUnit * monthlyUnits),
    monthlyRevenue: roundCurrency(sellingPrice * monthlyUnits),
    preAdvertisingProfit,
    profitMarginPercent,
    profitPerUnit,
    referralFee,
    returnsReserve,
    roiPercent,
    totalCostPerUnit,
  };
}
